import { CalendarCheck, CalendarX, Clock } from 'lucide-react';
import type { ParentAttendanceMonthStats } from '../../types/attendance';

export interface AttendanceMetricCardsProps {
  stats: ParentAttendanceMonthStats;
  /** The month the figures are for — "May 2026" — as the server named it. */
  monthLabel: string;
}

export function AttendanceMetricCards({ stats, monthLabel }: AttendanceMetricCardsProps) {
  // Null until a register exists. The gauge reads empty and the figure reads a
  // dash: a family whose school has not marked anything yet must not be shown
  // 0%, which reads as "your child attended nothing".
  const percent = stats.overallPercent ?? 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {/* Card 1: Overall Attendance (Emerald Variant) */}
      <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-emerald-200/90 dark:border-slate-800 bg-emerald-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
        <div>
          <p className="text-[14px] font-bold text-emerald-800 dark:text-emerald-400 leading-tight">
            Overall Attendance
          </p>
          <p className="mt-0.5 text-2xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight tabular-nums truncate">
            {stats.overallPercent === null ? '—' : `${stats.overallPercent}%`}
          </p>
        </div>

        {/* Progress Bar & Sub-stats */}
        <div className="mt-2.5">
          <div className="h-1.5 w-full rounded-full bg-emerald-200/80 dark:bg-slate-800 overflow-hidden">
            <div
              className="h-full rounded-full bg-[#059669] dark:bg-emerald-500 transition-all duration-700 ease-out"
              style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[11px] sm:text-xs font-semibold text-emerald-700/90 dark:text-slate-400">
            <span>Present: {stats.presentDays} {stats.presentDays === 1 ? 'Day' : 'Days'}</span>
            <span>Working days: {stats.workingDays}</span>
          </div>
        </div>
      </div>

      {/* Card 2: Present Days (Indigo Variant) */}
      <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-indigo-200/90 dark:border-slate-800 bg-indigo-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-bold text-indigo-800 dark:text-indigo-400 leading-tight">
              Present Days
            </p>
            <p className="mt-0.5 text-2xl font-black text-indigo-600 dark:text-indigo-400 tracking-tight tabular-nums truncate">
              {stats.presentDays}
            </p>
            <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-semibold text-indigo-600/90 dark:text-slate-400">
              {monthLabel}
            </p>
          </div>
          <span
            className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
            style={{ backgroundColor: '#4f46e5' }}
            aria-hidden
          >
            <CalendarCheck className="h-5 w-5" />
          </span>
        </div>
      </div>

      {/* Card 3: Absent Days (Rose Variant) */}
      <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-rose-200/90 dark:border-slate-800 bg-rose-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-bold text-rose-800 dark:text-rose-400 leading-tight">
              Absent Days
            </p>
            <p className="mt-0.5 text-2xl font-black text-rose-600 dark:text-rose-400 tracking-tight tabular-nums truncate">
              {stats.absentDays}
            </p>
            <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-semibold text-rose-600/90 dark:text-slate-400">
              {monthLabel}
            </p>
          </div>
          <span
            className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
            style={{ backgroundColor: '#e11d48' }}
            aria-hidden
          >
            <CalendarX className="h-5 w-5" />
          </span>
        </div>
      </div>

      {/* Card 4: Late Days (Cyan/Sky Variant) */}
      <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-cyan-200/90 dark:border-slate-800 bg-cyan-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-bold text-cyan-800 dark:text-cyan-400 leading-tight">
              Late Days
            </p>
            <p className="mt-0.5 text-2xl font-black text-cyan-600 dark:text-cyan-400 tracking-tight tabular-nums truncate">
              {stats.lateDays}
            </p>
            <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-semibold text-cyan-600/90 dark:text-slate-400">
              {monthLabel}
            </p>
          </div>
          <span
            className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
            style={{ backgroundColor: '#0891b2' }}
            aria-hidden
          >
            <Clock className="h-5 w-5" />
          </span>
        </div>
      </div>
    </div>
  );
}
